import { useState, useEffect, useRef } from "react";
import type { ProductDetail } from "@/mocks/productDetails";
import { useCart } from "@/hooks/useCart";
import { useThemeColor } from "@/hooks/useThemeColor";
import { supabase } from "@/lib/supabase";
import NotifyMeButton from "@/pages/product/components/NotifyMeButton";

interface ProductInfoProps {
  product: ProductDetail;
}

/* ── Live Stock Badge ── */
function LiveStockBadge({ stock, loading }: { stock: number; loading: boolean }) {
  const { hexAlpha, primary } = useThemeColor();

  if (loading) {
    return (
      <span
        className="flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full animate-pulse"
        style={{ background: hexAlpha(primary, 0.08), border: `1px solid ${hexAlpha(primary, 0.15)}`, color: hexAlpha(primary, 0.5), fontFamily: "'Tajawal', sans-serif" }}
      >
        <i className="ri-loader-4-line animate-spin text-xs"></i>
        جاري التحقق...
      </span>
    );
  }

  if (stock === 0) {
    return (
      <span className="flex items-center gap-1.5 bg-red-500/10 border border-red-500/20 text-red-400 text-xs font-bold px-3 py-1 rounded-full" style={{ fontFamily: "'Tajawal', sans-serif" }}>
        <i className="ri-close-circle-fill text-xs"></i>
        نفد المخزون
      </span>
    );
  }

  if (stock <= 3) {
    return (
      <span className="flex items-center gap-1.5 bg-red-500/10 border border-red-500/25 text-red-400 text-xs font-bold px-3 py-1 rounded-full" style={{ fontFamily: "'Tajawal', sans-serif" }}>
        <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-ping inline-block flex-shrink-0"></span>
        آخر {stock} {stock === 1 ? "قطعة" : "قطع"}! أسرع
      </span>
    );
  }

  if (stock <= 10) {
    return (
      <span className="flex items-center gap-1.5 bg-amber-400/10 border border-amber-400/20 text-amber-400 text-xs font-bold px-3 py-1 rounded-full" style={{ fontFamily: "'Tajawal', sans-serif" }}>
        <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse inline-block flex-shrink-0"></span>
        باقي {stock} فقط!
      </span>
    );
  }

  return (
    <span className="flex items-center gap-1.5 bg-emerald-400/10 border border-emerald-400/20 text-emerald-400 text-xs font-bold px-3 py-1 rounded-full" style={{ fontFamily: "'Tajawal', sans-serif" }}>
      <i className="ri-checkbox-circle-fill text-xs"></i>
      متوفر ({stock} قطعة)
    </span>
  );
}

/* ── Stock Progress Bar ── */
function StockProgressBar({ stock, maxStock, loading }: { stock: number; maxStock: number; loading: boolean }) {
  const { primary, hexAlpha } = useThemeColor();
  if (loading || maxStock <= 0) return null;

  const pct = Math.min(100, Math.round((stock / maxStock) * 100));
  const barColor = stock === 0 ? "#ef4444" : stock <= 3 ? "#ef4444" : stock <= 10 ? "#f59e0b" : "#10b981";

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between">
        <span className="text-gray-500 text-[11px]" style={{ fontFamily: "'Tajawal', sans-serif" }}>
          مستوى المخزون
        </span>
        <span className="text-xs font-bold" style={{ color: barColor, fontFamily: "'Tajawal', sans-serif" }}>
          {pct}%
        </span>
      </div>
      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: hexAlpha(primary, 0.08) }}>
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${pct}%`, background: barColor }}
        ></div>
      </div>
    </div>
  );
}

export default function ProductInfo({ product }: ProductInfoProps) {
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [activeTab, setActiveTab] = useState<"specs" | "box">("specs");
  const { addItem } = useCart();
  const { primary, primaryLight: light, primaryDark: dark, hexAlpha } = useThemeColor();

  /* ── Live stock state ── */
  const [liveStock, setLiveStock] = useState<number>(product.stock ?? 0);
  const [stockLoading, setStockLoading] = useState(true);
  const [maxStock, setMaxStock] = useState<number>(Math.max(product.stock ?? 0, 20));
  const prevStockRef = useRef<number | null>(null);
  const [stockChanged, setStockChanged] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchStock = async () => {
      try {
        const { data } = await supabase
          .from("products")
          .select("stock")
          .eq("id", product.id)
          .maybeSingle();

        if (cancelled) return;

        const fresh = data?.stock ?? 0;

        // Flash animation when stock changes
        if (prevStockRef.current !== null && prevStockRef.current !== fresh) {
          setStockChanged(true);
          setTimeout(() => setStockChanged(false), 800);
        }
        prevStockRef.current = fresh;

        setLiveStock(fresh);
        setMaxStock((prev) => Math.max(prev, fresh, 1));
        // Clamp qty to available stock
        setQty((q) => Math.min(q, fresh > 0 ? fresh : 1));
      } catch {
        /* silent */
      } finally {
        if (!cancelled) setStockLoading(false);
      }
    };

    fetchStock();

    // Poll every 30s for real-time feel
    const interval = setInterval(fetchStock, 30_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [product.id]);

  const handleAddToCart = () => {
    if (liveStock === 0) return;
    addItem({
      id: product.id,
      nameAr: product.nameAr,
      price: product.price,
      image: product.images[0],
      category: product.category,
      stock: liveStock,
    }, qty);
    setAdded(true);
    setTimeout(() => setAdded(false), 2500);
  };

  const discount = Math.round((1 - product.price / product.originalPrice) * 100);

  return (
    <div className="flex flex-col gap-6 w-full text-right" dir="rtl">

      {/* Category + Badges */}
      <div className="flex items-center gap-3 flex-wrap">
        <span
          className="text-sm font-semibold tracking-widest uppercase"
          style={{ fontFamily: "'Tajawal', sans-serif", color: hexAlpha(primary, 0.8) }}
        >
          {product.category}
        </span>
        {product.badge && (
          <span
            className="text-black text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap"
            style={{ fontFamily: "'Tajawal', sans-serif", background: `linear-gradient(to right, ${light}, ${primary})` }}
          >
            {product.badge}
          </span>
        )}
        {discount > 0 && (
          <span
            className="bg-red-500/15 text-red-400 border border-red-500/25 text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap"
            style={{ fontFamily: "'Tajawal', sans-serif" }}
          >
            وفّر {discount}%
          </span>
        )}
      </div>

      {/* Product Name */}
      <h1
        className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight"
        style={{ fontFamily: "'Amiri', 'Tajawal', serif", textShadow: "0 2px 20px rgba(0,0,0,0.5)" }}
      >
        {product.nameAr}
      </h1>

      {/* Rating */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1">
          {Array.from({ length: 5 }).map((_, i) => (
            <i key={i} className="ri-star-fill text-base" style={{ color: i < product.rating ? primary : "#374151" }}></i>
          ))}
        </div>
        <span className="text-gray-400 text-sm" style={{ fontFamily: "'Tajawal', sans-serif" }}>
          ({product.reviews} تقييم)
        </span>
        {/* Live stock badge with flash animation */}
        <span className={`transition-all duration-300 ${stockChanged ? "scale-110" : "scale-100"}`}>
          <LiveStockBadge stock={liveStock} loading={stockLoading} />
        </span>
      </div>

      {/* Stock Progress Bar */}
      {!stockLoading && (
        <StockProgressBar stock={liveStock} maxStock={maxStock} loading={stockLoading} />
      )}

      {/* Short desc */}
      <p className="text-gray-400 text-sm leading-relaxed" style={{ fontFamily: "'Tajawal', sans-serif", fontWeight: 300 }}>
        {product.shortDesc}
      </p>

      {/* Price Box */}
      <div
        className="bg-gradient-to-br from-[#141414] to-[#111] rounded-2xl p-5 flex items-center justify-between flex-wrap gap-4"
        style={{ border: `1px solid ${hexAlpha(primary, 0.15)}`, boxShadow: `0 0 30px ${hexAlpha(primary, 0.04)}` }}
      >
        <div>
          <p className="text-gray-500 text-xs mb-2" style={{ fontFamily: "'Tajawal', sans-serif" }}>السعر</p>
          <div className="flex items-baseline gap-3">
            <span
              className="text-4xl md:text-5xl font-bold"
              style={{
                fontFamily: "'Amiri', 'Tajawal', serif",
                background: `linear-gradient(to left, ${light}, ${primary}, ${dark})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                filter: `drop-shadow(0 0 12px ${hexAlpha(primary, 0.3)})`,
              }}
            >
              {product.price.toLocaleString()}
            </span>
            <span className="text-lg font-semibold" style={{ fontFamily: "'Tajawal', sans-serif", color: hexAlpha(primary, 0.7) }}>ر.س</span>
            {product.originalPrice > product.price && (
              <span className="text-gray-600 text-lg line-through" style={{ fontFamily: "'Tajawal', sans-serif" }}>
                {product.originalPrice.toLocaleString()}
              </span>
            )}
          </div>
        </div>

        {/* Qty */}
        <div className="flex items-center gap-3">
          <p className="text-gray-400 text-xs" style={{ fontFamily: "'Tajawal', sans-serif" }}>الكمية:</p>
          <div className="flex items-center rounded-full overflow-hidden bg-[#0f0f0f]" style={{ border: `1px solid ${hexAlpha(primary, 0.2)}` }}>
            <button
              onClick={() => setQty((q) => Math.max(1, q - 1))}
              disabled={liveStock === 0}
              className="w-10 h-10 flex items-center justify-center text-gray-400 transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ transition: "color 0.2s, background 0.2s" }}
              onMouseEnter={(e) => { if (liveStock > 0) { (e.currentTarget as HTMLElement).style.color = primary; (e.currentTarget as HTMLElement).style.background = hexAlpha(primary, 0.1); }}}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = ""; (e.currentTarget as HTMLElement).style.background = ""; }}
            >
              <i className="ri-subtract-line text-sm"></i>
            </button>
            <span className="w-10 text-center text-white font-bold text-sm" style={{ fontFamily: "'Tajawal', sans-serif" }}>
              {qty}
            </span>
            <button
              onClick={() => setQty((q) => liveStock > 0 ? Math.min(liveStock, q + 1) : q)}
              disabled={liveStock === 0 || qty >= liveStock}
              className="w-10 h-10 flex items-center justify-center text-gray-400 transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
              onMouseEnter={(e) => { if (liveStock > 0 && qty < liveStock) { (e.currentTarget as HTMLElement).style.color = primary; (e.currentTarget as HTMLElement).style.background = hexAlpha(primary, 0.1); }}}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = ""; (e.currentTarget as HTMLElement).style.background = ""; }}
            >
              <i className="ri-add-line text-sm"></i>
            </button>
          </div>
          {/* max qty hint */}
          {!stockLoading && liveStock > 0 && liveStock <= 10 && (
            <span className="text-[11px] text-amber-400/70" style={{ fontFamily: "'Tajawal', sans-serif" }}>
              الحد الأقصى {liveStock}
            </span>
          )}
        </div>
      </div>

      {/* CTA Buttons */}
      {!stockLoading && liveStock === 0 ? (
        /* ── نفد المخزون: عرض زر الإشعار + واتساب ── */
        <div className="flex flex-col gap-3" dir="rtl">
          {/* بانر نفاد المخزون */}
          <div
            className="flex items-center gap-3 rounded-2xl px-5 py-3"
            style={{ background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.2)" }}
          >
            <div className="w-8 h-8 flex items-center justify-center bg-red-500/15 rounded-full flex-shrink-0">
              <i className="ri-close-circle-fill text-red-400 text-base"></i>
            </div>
            <div className="flex-1">
              <p className="text-red-400 font-black text-sm" style={{ fontFamily: "'Tajawal', sans-serif" }}>
                هذا المنتج غير متوفر حالياً
              </p>
              <p className="text-red-400/60 text-xs mt-0.5" style={{ fontFamily: "'Tajawal', sans-serif" }}>
                سجّل رقمك وسنُبلغك فور عودته للمخزون
              </p>
            </div>
          </div>

          {/* زر أشعرني */}
          <NotifyMeButton productId={product.id} productName={product.nameAr} />

          {/* واتساب */}
          <a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              window.history.back();
              setTimeout(() => {
                document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
              }, 300);
            }}
            className="flex items-center justify-center gap-2 py-3.5 rounded-full font-bold text-sm transition-all whitespace-nowrap cursor-pointer"
            style={{ fontFamily: "'Tajawal', sans-serif", border: `1px solid ${hexAlpha(primary, 0.3)}`, color: primary }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = hexAlpha(primary, 0.1); (e.currentTarget as HTMLElement).style.borderColor = hexAlpha(primary, 0.6); }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = ""; (e.currentTarget as HTMLElement).style.borderColor = hexAlpha(primary, 0.3); }}
          >
            <i className="ri-whatsapp-line text-base"></i>
            تواصل معنا عبر واتساب
          </a>
        </div>
      ) : (
        /* ── متوفر: عرض زر السلة + واتساب ── */
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleAddToCart}
            disabled={stockLoading}
            className={`group flex-1 relative flex items-center justify-center gap-2 py-4 rounded-full font-bold text-sm transition-all duration-300 whitespace-nowrap cursor-pointer overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed ${added ? "bg-emerald-500 text-white" : "text-black"}`}
            style={!added ? { background: `linear-gradient(to right, ${light}, ${primary})` } : {}}
            onMouseEnter={(e) => { if (!added) (e.currentTarget as HTMLElement).style.background = `linear-gradient(to right, ${primary}, ${dark})`; }}
            onMouseLeave={(e) => { if (!added) (e.currentTarget as HTMLElement).style.background = `linear-gradient(to right, ${light}, ${primary})`; }}
          >
            <span className="relative z-10 flex items-center gap-2" style={{ fontFamily: "'Tajawal', sans-serif" }}>
              <i className={added ? "ri-check-line" : stockLoading ? "ri-loader-4-line animate-spin" : "ri-shopping-cart-line"}></i>
              {added ? "تمت الإضافة للسلة!" : stockLoading ? "جاري التحقق..." : "أضف للسلة"}
            </span>
            {!added && !stockLoading && (
              <div className="absolute inset-0 bg-white/20 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-500"></div>
            )}
          </button>
          <a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              window.history.back();
              setTimeout(() => {
                document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
              }, 300);
            }}
            className="flex-1 flex items-center justify-center gap-2 py-4 rounded-full font-bold text-sm transition-all whitespace-nowrap cursor-pointer"
            style={{ fontFamily: "'Tajawal', sans-serif", border: `1px solid ${hexAlpha(primary, 0.3)}`, color: primary }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = hexAlpha(primary, 0.1); (e.currentTarget as HTMLElement).style.borderColor = hexAlpha(primary, 0.6); }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = ""; (e.currentTarget as HTMLElement).style.borderColor = hexAlpha(primary, 0.3); }}
          >
            <i className="ri-whatsapp-line"></i>
            اطلب عبر واتساب
          </a>
        </div>
      )}

      {/* Trust Badges */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { icon: "ri-truck-line", title: "شحن مجاني", sub: "لجميع الطلبات", color: "text-emerald-400", border: "border-emerald-500/20", bg: "bg-emerald-500/5" },
          { icon: "ri-money-dollar-circle-line", title: "الدفع عند الاستلام", sub: "في جميع المناطق", color: "", border: "", bg: "" },
          { icon: "ri-shield-check-line", title: "ضمان أصلي", sub: "من الشركة الأم", color: "text-sky-400", border: "border-sky-400/20", bg: "bg-sky-400/5" },
          { icon: "ri-exchange-line", title: "إرجاع مجاني", sub: "خلال 7 أيام", color: "text-rose-400", border: "border-rose-400/20", bg: "bg-rose-400/5" },
        ].map((badge, idx) => (
          <div
            key={badge.title}
            className={`flex flex-col items-center text-center gap-2 rounded-xl p-4 transition-all hover:scale-[1.02] ${badge.bg} ${badge.border ? `border ${badge.border}` : ""}`}
            style={idx === 1 ? { background: hexAlpha(primary, 0.05), border: `1px solid ${hexAlpha(primary, 0.2)}` } : {}}
          >
            <div className="w-8 h-8 flex items-center justify-center">
              <i className={`${badge.icon} text-xl ${badge.color}`} style={idx === 1 ? { color: primary } : {}}></i>
            </div>
            <div>
              <p className={`font-bold text-xs leading-tight ${badge.color}`} style={{ fontFamily: "'Tajawal', sans-serif", ...(idx === 1 ? { color: primary } : {}) }}>
                {badge.title}
              </p>
              <p className="text-gray-500 text-[10px] mt-0.5" style={{ fontFamily: "'Tajawal', sans-serif" }}>
                {badge.sub}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Highlights */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {product.highlights.map((h, i) => (
          <div key={i} className="flex items-center gap-3 bg-[#111] border border-white/5 rounded-xl px-4 py-3 transition-all"
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = hexAlpha(primary, 0.15); }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = "rgba(255,255,255,0.05)"; }}
          >
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <i className="ri-check-double-line text-sm" style={{ color: primary }}></i>
            </div>
            <span className="text-gray-300 text-sm" style={{ fontFamily: "'Tajawal', sans-serif" }}>{h}</span>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="mt-2">
        <div className="flex border-b border-white/10 mb-5">
          {[
            { key: "specs", label: "المواصفات الكاملة", icon: "ri-list-check-3" },
            { key: "box", label: "محتويات الصندوق", icon: "ri-box-3-line" },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as "specs" | "box")}
              className="flex items-center gap-2 pb-3 px-4 text-sm font-semibold transition-all whitespace-nowrap cursor-pointer border-b-2"
              style={{
                fontFamily: "'Tajawal', sans-serif",
                borderBottomColor: activeTab === tab.key ? primary : "transparent",
                color: activeTab === tab.key ? primary : "#6b7280",
              }}
            >
              <i className={tab.icon}></i>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Specs Table */}
        {activeTab === "specs" && (
          <div className="flex flex-col gap-1">
            {product.specs.map((spec, i) => (
              <div
                key={i}
                className={`flex items-start gap-4 px-4 py-3 rounded-xl transition-all ${i % 2 === 0 ? "bg-[#111]" : "bg-transparent"}`}
              >
                <span className="text-gray-500 text-sm w-40 flex-shrink-0" style={{ fontFamily: "'Tajawal', sans-serif" }}>
                  {spec.label}
                </span>
                <span className="text-gray-200 text-sm font-medium flex-1" style={{ fontFamily: "'Tajawal', sans-serif" }}>
                  {spec.value}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* In Box */}
        {activeTab === "box" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {product.inBox.map((item, i) => (
              <div key={i} className="flex items-center gap-3 bg-[#111] border border-white/5 rounded-xl px-4 py-3 transition-all"
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = hexAlpha(primary, 0.15); }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = "rgba(255,255,255,0.05)"; }}
              >
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-archive-line text-sm" style={{ color: primary }}></i>
                </div>
                <span className="text-gray-300 text-sm" style={{ fontFamily: "'Tajawal', sans-serif" }}>{item}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Long desc */}
      <div className="border-t border-white/5 pt-5">
        <h3 className="text-white font-bold text-lg mb-3" style={{ fontFamily: "'Amiri', 'Tajawal', serif" }}>
          وصف المنتج
        </h3>
        <p className="text-gray-400 text-sm leading-loose" style={{ fontFamily: "'Tajawal', sans-serif", fontWeight: 300 }}>
          {product.longDesc}
        </p>
      </div>

    </div>
  );
}
